import React, { FC, useEffect, useState } from 'react'
import { useGeolocation } from 'react-use'
import LocationPinIcon from 'transfix-icons/build/LocationPinIcon'
import { useGeocodingSearch } from 'transfix-maps/hooks/useGeocodingSearch'
import SuggestLocation, { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { TToastNewState } from 'transfix-ui/components/Toast'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useBrowserLocationContext } from 'apps/fleet-planner/context/BrowserLocationContext'
import { Button, FormModal } from 'transfix-ui'

const useStyles = makeStyles(theme => ({
  text: {
    [theme.breakpoints.down('md')]: {
      color: brandColors.coolGray5,
    },
  },
  suggestLocation: {
    [theme.breakpoints.up('md')]: {
      width: '50%',
    },
  },
  button: {
    textAlign: 'left',
    fontWeight: 600,
    paddingLeft: 0,
    '& > .MuiButton-label': {
      display: 'block',
    },
  },
}))

interface IChangeMyLocationModalProps {
  /**
   * Is the modal visible?
   * @default false
   */
  isOpen: boolean
  /**
   * On close modal handler
   */
  onClose: () => void
  /**
   * On submit a new location information
   */
  onSubmitNewLocation: (toastState: TToastNewState) => void
}

export const ChangeMyLocationModal: FC<IChangeMyLocationModalProps> = ({
  isOpen = false,
  onClose,
  onSubmitNewLocation,
}) => {
  const { browserLocation, setBrowserLocation } = useBrowserLocationContext()
  // if there is a previously set location, display that in the autosuggest bar
  const { cityName, stateCode } = browserLocation || {}
  const { isMobile } = useBreakpointContext()
  const classes = useStyles({ isOpen })
  const geolocation = useGeolocation()
  const { results: mapResults = [], getResults } = useGeocodingSearch()

  const [suggestLocationValue, setSuggestLocationValue] = useState<ILocationSuggestion>({
    city: cityName,
    state: stateCode,
  })

  // Update browser location info using mapbox if the geolocation changes
  useEffect(() => {
    const { latitude, longitude } = geolocation || {}
    async function fetchResults() {
      await getResults('', { latitude, longitude })
    }

    if (typeof latitude === 'number' && typeof longitude === 'number') {
      fetchResults()
    }
  }, [geolocation, getResults])

  /**
   * Take the information in LocationSuggest bar and save it to local storage
   */
  const confirmActionHandler = () => {
    if (suggestLocationValue) {
      const { city, coords, state } = suggestLocationValue || {}
      const { lat, lon } = coords || {}
      setBrowserLocation({
        lat,
        lon,
        cityName: city,
        stateCode: state,
      })
    }
    onClose()
    onSubmitNewLocation({
      toastMessage: 'Location Updated',
    })
  }

  const cancelActionHandler = () => {
    // clear unconfirmed search results
    setSuggestLocationValue({
      city: cityName,
      state: stateCode,
    })
    onClose()
  }

  const suggestLocationChangeHandler = (locationSuggestion: ILocationSuggestion) => {
    setSuggestLocationValue(locationSuggestion)
  }

  const autoDetectMyLocationClickHandler = () => {
    /**
     * If this option is selected, we will display the auto-detected browser location
     * which we got from the useGeolocation() hook
     */
    const [firstLocation] = mapResults || []
    const { latitude, longitude } = geolocation || {}
    const { city, state } = firstLocation || {}
    if (longitude && latitude) {
      const newLocationSuggestion = {
        coords: {
          lon: longitude,
          lat: latitude,
        },
        city,
        state,
      }
      setSuggestLocationValue(newLocationSuggestion)
    }
  }

  return (
    <FormModal
      open={isOpen}
      title='Change my location'
      onClose={cancelActionHandler}
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
        },
        {
          label: 'Save Location',
          type: 'confirm',
          action: confirmActionHandler,
        },
      ]}>
      <Box mb={isMobile ? 4 : 2}>
        <Typography variant='body1' className={classes.text}>
          Update your location to quickly search for loads near you
        </Typography>
      </Box>

      <Box mb={1} className={classes.suggestLocation}>
        <SuggestLocation
          label='My location'
          countries={['US', 'CA', 'MX']}
          resultTypes={['City', 'State', 'PostalCode']}
          placeholder='Enter a city, or zip'
          onChange={suggestLocationChangeHandler}
          value={suggestLocationValue}
        />
      </Box>

      <Button
        color='tertiary'
        className={classes.button}
        onClick={autoDetectMyLocationClickHandler}
        size='large'>
        <span className='MuiButton-label'>
          <Box display='flex' flexDirection='row' alignContent='center'>
            <div>
              <LocationPinIcon size='large' />
            </div>
            <Box pl={0.5}>Auto-detect my location</Box>
          </Box>
        </span>
      </Button>
    </FormModal>
  )
}
